"use client";

/**
 * <Timestamp> — the only thing UI code should use to render a time.
 *
 * Renders a localized label in the user's primary timezone (default) or
 * the data region's primary timezone (when `dataset` is set + `kind="data"`),
 * with a hover/focus tooltip showing additional context.
 *
 * Tooltip layout depends on `kind` + `dataset`:
 *
 *   kind="system" (audit logs, sessions, invitations, account security)
 *     ┌──────────────────────┐
 *     │ Your time            │  ← primary, in user's resolved TZ
 *     │ 25 Apr 01:22 CET     │
 *     │                      │
 *     │ Company time         │  ← secondary, in company TZ (or LATAM for GA)
 *     │ 24 Apr 19:22 ET      │
 *     └──────────────────────┘
 *
 *   kind="data" + dataset="LATAM" (a single row in a data-bound table /
 *   "last seen" column / aggregated metric — anything tied to a dataset)
 *     ┌──────────────────────┐
 *     │ Latin America time   │  ← primary, in the region's native TZ
 *     │ 24 Apr 19:22 BRT     │
 *     │                      │
 *     │ Your timezones       │  ← user + company shown together
 *     │ You    25 Apr 01:22  │
 *     │ Co.    24 Apr 19:22  │
 *     └──────────────────────┘
 *
 *   kind="data" without dataset (legacy fallback — dashboards that haven't
 *   declared a region yet) keeps the pre-region behaviour: primary is the
 *   user's TZ, secondary is company / LATAM regional.
 *
 * For chart axes, exports, and query-filter labels — anywhere that needs
 * a string rather than a React node — use `formatChartTimestamp(iso, tz)`
 * with `tz` from `useChartTimezone(regionId)`.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { getCompany } from "@/lib/api/admin";
import {
  formatTimestamp,
  tzAbbrev,
  type TimestampVariant,
} from "./format";
import { useTimezones } from "./provider";
import { getRegion, type RegionId } from "./regions";

export interface TimestampProps {
  /** ISO 8601 string (UTC). Null/empty renders "-". */
  value: string | null | undefined;
  /** Layout preset for the visible label. Default: "short". */
  variant?: TimestampVariant;
  /** Tooltip layout. Default: "system". */
  kind?: "system" | "data";
  /**
   * The data region this timestamp belongs to. Only meaningful with
   * `kind="data"`. When provided, the visible label and tooltip primary
   * row use the region's native timezone, and user/company timezones
   * move into the secondary slot. Use this for any timestamp that is
   * "data-shaped" — bucket boundaries, last-seen markers, signal
   * timestamps, dataset metadata.
   */
  dataset?: RegionId;
  /** Show the timezone abbreviation next to the label (e.g. "01:22 CET"). Default: true. */
  showAbbrev?: boolean;
  /** When a global admin is viewing a specific company's pages, pass
   *  that company's id so the tooltip shows that company's timezone
   *  instead of the LATAM regional fallback. */
  companyId?: string;
  /** Extra classes on the outer wrapper (positioning / spacing). */
  className?: string;
}

interface TooltipBlock {
  heading: string;
  rows: Array<{ label: string; tz: string }>;
}

export function Timestamp({
  value,
  variant = "short",
  kind = "system",
  dataset,
  showAbbrev = true,
  companyId,
  className,
}: TimestampProps) {
  const tz = useTimezones();

  // Optional GA-in-company override: fetch the specific company's TZ.
  // When companyId is undefined this query is disabled (no extra fetch).
  const { data: contextCompany } = useQuery({
    queryKey: ["admin", "company", companyId],
    queryFn: () => getCompany(companyId ?? ""),
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
  });
  const overrideCompanyTz = contextCompany?.timezone ?? null;

  if (!value) {
    return <span className={className}>-</span>;
  }

  // Region only matters for kind="data". Resolved up-front so the
  // tooltip / label code below is a straight branch.
  const region = kind === "data" && dataset ? getRegion(dataset) : null;

  const labelTz = region ? region.primaryTimezone : tz.primary;
  const label = formatTimestamp(value, labelTz, variant, tz.locale);
  const primaryAbbrev = showAbbrev ? tzAbbrev(labelTz, value, tz.locale) : "";

  const blocks: TooltipBlock[] = region
    ? buildDataTooltip({
        region,
        userTz: tz.primary,
        companyTz: overrideCompanyTz ?? tz.company,
        companyName: contextCompany?.name ?? null,
      })
    : kind === "system"
    ? buildSystemTooltip({
        userTz: tz.primary,
        companyTz: overrideCompanyTz ?? tz.company,
        regional: tz.regional,
        isGA: tz.isGlobalAdminContext,
        browserTz: tz.browser,
        companyName: contextCompany?.name ?? null,
      })
    : buildLegacyDataTooltip({
        browserTz: tz.browser,
        companyTz: overrideCompanyTz ?? tz.company,
        regional: tz.regional,
        isGA: tz.isGlobalAdminContext,
        companyName: contextCompany?.name ?? null,
      });

  return (
    <TimestampTrigger
      label={label}
      primaryAbbrev={primaryAbbrev}
      blocks={blocks}
      value={value}
      locale={tz.locale}
      className={className}
    />
  );
}

/**
 * Trigger + portal-rendered tooltip. The tooltip lives at document.body
 * with `position: fixed`, so overflow-clipping ancestors (e.g. the
 * `overflow-x-auto` wrapper around audit log tables) cannot crop it.
 */
function TimestampTrigger({
  label,
  primaryAbbrev,
  blocks,
  value,
  locale,
  className,
}: {
  label: string;
  primaryAbbrev: string;
  blocks: TooltipBlock[];
  value: string;
  locale: string;
  className?: string | undefined;
}) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Position the tooltip above the trigger, clamped to the viewport so
  // it doesn't run off the left/right edge.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const margin = 8;
    const desiredLeft =
      triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - margin;
    const left = Math.max(margin, Math.min(desiredLeft, maxLeft));
    const top = triggerRect.top - tooltipRect.height - margin;
    setCoords({ top, left });
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex cursor-default ${className ?? ""}`}
      tabIndex={0}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span>
        {label}
        {primaryAbbrev ? (
          <span className="ml-1 text-text-tertiary">{primaryAbbrev}</span>
        ) : null}
      </span>

      {open && mounted
        ? createPortal(
            <span
              ref={tooltipRef}
              role="tooltip"
              className="pointer-events-none fixed z-1000 whitespace-nowrap rounded-lg border border-border bg-surface px-3 py-2 text-left text-xs shadow-lg"
              style={{
                top: coords?.top ?? -9999,
                left: coords?.left ?? -9999,
                visibility: coords ? "visible" : "hidden",
              }}
            >
              {blocks.map((block, idx) => (
                <span key={`${block.heading}-${idx}`} className="block">
                  {idx > 0 ? (
                    <span className="my-1.5 block h-px bg-border" />
                  ) : null}
                  <TooltipBlockRow block={block} value={value} locale={locale} />
                </span>
              ))}
              <span className="mt-1.5 block border-t border-border pt-1 text-[10px] text-text-tertiary">
                UTC: {formatTimestamp(value, "UTC", "long", locale)}
              </span>
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

function buildSystemTooltip(args: {
  userTz: string;
  companyTz: string | null;
  regional: ReadonlyArray<{ tz: string; label: string }>;
  isGA: boolean;
  browserTz: string;
  companyName: string | null;
}): TooltipBlock[] {
  const { userTz, companyTz, regional, isGA, browserTz, companyName } = args;
  const primary: TooltipBlock = {
    heading: "Your time",
    rows: [{ label: "", tz: userTz }],
  };
  if (companyTz) {
    return [
      primary,
      {
        heading: "Company time",
        rows: [{ label: companyName ?? "Company", tz: companyTz }],
      },
    ];
  }
  if (isGA && regional.length > 0) {
    return [
      primary,
      {
        heading: "Latin America",
        rows: regional.map((r) => ({ label: r.label, tz: r.tz })),
      },
    ];
  }
  return [
    primary,
    { heading: "Browser time", rows: [{ label: "Browser", tz: browserTz }] },
  ];
}

function buildDataTooltip(args: {
  region: { id: string; label: string; primaryTimezone: string; secondaryTimezones: ReadonlyArray<{ tz: string; label: string }> };
  userTz: string;
  companyTz: string | null;
  companyName: string | null;
}): TooltipBlock[] {
  const { region, userTz, companyTz, companyName } = args;
  const primary: TooltipBlock = {
    heading: `${region.label} time`,
    rows: [
      { label: "", tz: region.primaryTimezone },
      ...region.secondaryTimezones.map((r) => ({ label: r.label, tz: r.tz })),
    ],
  };
  const yourRows: Array<{ label: string; tz: string }> = [
    { label: "You", tz: userTz },
  ];
  if (companyTz && companyTz !== userTz) {
    yourRows.push({ label: companyName ?? "Company", tz: companyTz });
  }
  return [primary, { heading: "Your timezones", rows: yourRows }];
}

function buildLegacyDataTooltip(args: {
  browserTz: string;
  companyTz: string | null;
  regional: ReadonlyArray<{ tz: string; label: string }>;
  isGA: boolean;
  companyName: string | null;
}): TooltipBlock[] {
  const { browserTz, companyTz, regional, isGA, companyName } = args;
  const primary: TooltipBlock = {
    heading: "Your browser",
    rows: [{ label: "", tz: browserTz }],
  };
  if (companyTz) {
    return [
      primary,
      {
        heading: "Company time",
        rows: [{ label: companyName ?? "Company", tz: companyTz }],
      },
    ];
  }
  if (isGA && regional.length > 0) {
    return [
      primary,
      {
        heading: "Latin America",
        rows: regional.map((r) => ({ label: r.label, tz: r.tz })),
      },
    ];
  }
  return [primary];
}

function TooltipBlockRow({
  block,
  value,
  locale,
}: {
  block: TooltipBlock;
  value: string;
  locale: string;
}) {
  return (
    <span className="block">
      <span className="block text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
        {block.heading}
      </span>
      {block.rows.map((row) => (
        <span key={row.tz} className="mt-0.5 block text-text-primary">
          {row.label ? (
            <span className="mr-2 inline-block w-14 text-text-tertiary">
              {row.label}
            </span>
          ) : null}
          <span className="font-mono">
            {formatTimestamp(value, row.tz, "long", locale)}
          </span>
          <span className="ml-1.5 text-text-tertiary">
            {tzAbbrev(row.tz, value, locale)}
          </span>
        </span>
      ))}
    </span>
  );
}
